import { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import type { MetricRule } from '@data/utils/resolve-metrics';
import type { AdminMetricType } from '@data/hooks/api/use-admin-metrics-api/use-admin-metrics-api';
import type { ColumnRule } from '@data/utils/resolve-columns';

/**
 * The Overview report's roster figures, shared by the teacher's own report and
 * the admin drill-down into one educator — the same metrics read through the
 * same `v2/metrics` route, differing only in whose roster is asked for.
 */
export const OVERVIEW_PRIMARY_METRICS: MetricRule[] = [
  { metric: 'TOTAL_STUDENT_SESSIONS' },
  { metric: 'TOTAL_STUDENT_MINUTES' },
  { metric: 'TOTAL_MINUTES_READING', products: ['LITPRO', 'R4R'] },
  {
    metric: 'LEXILE_MEASURE',
    products: ['LITPRO'],
    drilldowns: [Drilldown.Student],
  },
  {
    metric: 'GE_SCORE',
    products: ['R4R'],
    drilldowns: [Drilldown.Student],
    excludedByProductCodes: ['R4RWB'],
  },
  { metric: 'TOTAL_LITBITS_EARNED', products: ['LITPRO'] },
];

export const OVERVIEW_SECONDARY_METRICS: MetricRule[] = [
  { metric: 'AVG_ACTIVITY_SCORE' },
  { metric: 'ACTIVITIES_COMPLETED' },
  { metric: 'CONTENT_VIEWS' },
  { metric: 'CONTENT_COMPLETIONS' },
  { metric: 'DIGITAL_MINUTES_READ', products: ['LITPRO'] },
  { metric: 'MANUALLY_ENTERED_MINUTES', products: ['LITPRO'] },
];

export const OVERVIEW_TABLE_COLUMNS: ColumnRule[] = [
  { key: 'CLASS' },
  { key: 'STUDENT' },
  { key: 'TOTAL_STUDENT_SESSIONS', edApps: ['rise'] },
  { key: 'TOTAL_STUDENT_MINUTES' },
  { key: 'TOTAL_MINUTES_READING', edApps: ['LITPRO', 'R4R'] },
  { key: 'AVG_ACTIVITY_SCORE', edApps: ['WORD', 'FIRST', 'LITPRO', 'R4R'] },
  { key: 'TOTAL_LITBITS_EARNED', edApps: ['LITPRO'] },
];

/**
 * The admin Overview highlights: organization roll-ups from `v2/metrics/admin`.
 *
 * `drilldowns` records the report levels each roll-up is defined for. All
 * three admin levels read the same set today — every organization, one
 * organization, one educator — so a metric that later applies to only some of
 * them is a change here rather than in the page.
 *
 * Labels and tooltips are not listed here — the reporting service returns them
 * with each value, so there is one source for the wording.
 *
 * The design also calls for Total Minutes Reading, Total LitBits Earned,
 * Digital Minutes Read and Manually Entered Minutes. Those have no
 * organization-wide metric type on `v2/metrics/admin` yet — only the teacher
 * roster equivalents above exist — so they are left out rather than faked, and
 * can be added to these lists once the service exposes them.
 */
const ADMIN_LEVELS = [
  Drilldown.AllOrganizations,
  Drilldown.Organization,
  Drilldown.Teacher,
];

export const ADMIN_PRIMARY_METRICS: MetricRule<AdminMetricType>[] = [
  { metric: 'ACTIVE_EDUCATORS', drilldowns: ADMIN_LEVELS },
  { metric: 'ACTIVE_STUDENTS', drilldowns: ADMIN_LEVELS },
  { metric: 'ADMIN_STUDENT_SESSIONS', drilldowns: ADMIN_LEVELS },
  { metric: 'ADMIN_STUDENT_MINUTES', drilldowns: ADMIN_LEVELS },
];

export const ADMIN_SECONDARY_METRICS: MetricRule<AdminMetricType>[] = [
  { metric: 'ADMIN_AVG_ACTIVITY_SCORE', drilldowns: ADMIN_LEVELS },
  { metric: 'ADMIN_ACTIVITIES_COMPLETED', drilldowns: ADMIN_LEVELS },
  { metric: 'ADMIN_CONTENT_VIEWS', drilldowns: ADMIN_LEVELS },
  { metric: 'ADMIN_CONTENT_COMPLETIONS', drilldowns: ADMIN_LEVELS },
];
